import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { type BudgetDurationType, editBudgetGroup } from "shared";
import { toast } from "sonner";
import type z from "zod";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import { useAppForm } from "@/hooks/useCreateForm";
import { restrictRoute } from "@/lib/routeBlocker";
import { BudgetTypeInputs } from "./-components/budget-type-inputs";
import { SubBudgetInputs } from "./-components/sub-budgets-input";

export const Route = createFileRoute("/_authed/_org/budgets/$groupId/edit")({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/budgets",
      },
      requiredRole: "bookkeeper",
    }),
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();

  const { groupId } = Route.useParams();

  const { data: budgetGroup } = useSuspenseQuery(
    trpc.budget.get.queryOptions({
      groupId,
    }),
  );

  const { mutate, isPending } = useMutation(
    trpc.budget.edit.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async () => {
        await queryClient.refetchQueries(
          trpc.budget.get.queryOptions({
            groupId,
          }),
        );
        toast.success("Budget Updated", {
          position: "bottom-left",
        });
        form.reset();

        queryClient.invalidateQueries(trpc.budget.list.queryOptions());
      },
    }),
  );

  const defaultValues = {
    ...budgetGroup,
    groupId,
    subBudgets: budgetGroup.subBudgets.map((sb) => ({
      id: sb.id,
      name: sb.name,
      targetCents: sb.targetCents,
    })),
    timing: {
      end: budgetGroup.endDate ?? undefined,
      refreshInterval: budgetGroup.refreshInterval ?? undefined,
      start: budgetGroup.startDate ?? undefined,
      type:
        budgetGroup.refreshInterval &&
        budgetGroup.refreshInterval !== "lifetime"
          ? "repeating"
          : "once",
    } as BudgetDurationType,
    users: budgetGroup.budgetPermissions.map((p) => p.userId),
  } as z.infer<typeof editBudgetGroup>;

  const form = useAppForm({
    defaultValues,
    onSubmit: ({ value }) => {
      if (value.subBudgets.length === 1) value.subBudgets[0].name = value.name;

      mutate(value);
    },
    onSubmitInvalid: ({ formApi }) => {
      console.log("Form invalid", formApi.getAllErrors());
    },
    validators: {
      onSubmit: editBudgetGroup,
    },
  });

  const [resetCount, setResetCount] = useState(0);

  return (
    <form.AppForm>
      <form
        className="flex h-full min-h-0 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="min-h-0 grow space-y-4 overflow-y-auto px-6 pt-2 pb-4">
          <FormStepSeparator description="Details" />
          <form.AppField
            children={(field) => {
              return <field.TextField label="Name" required />;
            }}
            name="name"
          />
          {/* <form.AppField
            children={(field) => {
              return (
                <field.TextAreaField label="Description" required={false} />
              );
            }}
            name="description"
          /> */}

          <BudgetTypeInputs form={form} fields={{ timing: "timing" }} />

          <FormStepSeparator description="Control Spend" />

          <SubBudgetInputs
            form={form}
            fields={{
              subBudgets: "subBudgets",
            }}
            key={`sub-budgets-${resetCount}`}
          />

          <form.AppField
            children={(field) => (
              <field.UserMultipleSelectField
                label="Access"
                descriptionStyle="below"
                description="These users will be able to spend on this budget. Admins and Bookkeepers can spend on all budgets by default."
                required
              />
            )}
            name="users"
          />
        </div>

        <div className="flex min-h-16 flex-row items-center justify-around border-t p-6">
          <form.ConditionalSubmitCancelButtons
            isPending={isPending}
            onCancelClick={() => setResetCount((c) => c + 1)}
          />
        </div>
      </form>
    </form.AppForm>
  );
}
